import { useSearchHistory, type SearchHistoryItem } from '@/lib/store/searchHistory';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Hash, Shield, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RecentSearchesProps {
  onSelect: (query: string) => void;
  className?: string;
  searches?: SearchHistoryItem[];
}

function getSearchIcon(type?: string) {
  switch (type) {
    case 'hash':
      return <Hash className="h-3.5 w-3.5" />;
    case 'group':
      return <Shield className="h-3.5 w-3.5" />;
    default:
      return <Clock className="h-3.5 w-3.5" />;
  }
}

function formatTimestamp(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  
  // Less than a minute
  if (diff < 60000) {
    return 'Just now';
  }
  
  // Less than an hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes}m ago`;
  }
  
  // Less than a day
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  }
  
  // More than a day
  const days = Math.floor(diff / 86400000);
  return `${days}d ago`;
}

export function RecentSearches({ onSelect, className, searches }: RecentSearchesProps) {
  const clearHistory = useSearchHistory(state => state.clearHistory);
  const searchItems = searches || useSearchHistory(state => state.searches);
  
  if (!searchItems.length) return null;

  return (
    <Card className={cn("mt-2", className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-muted-foreground">Recent Searches</h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-1 text-xs hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              clearHistory();
            }}
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {searchItems.map((search: SearchHistoryItem) => (
            <button
              key={`${search.query}-${search.timestamp}`}
              onClick={() => onSelect(search.query)}
              className={cn(
                "group flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm transition-colors",
                "bg-accent hover:bg-accent/80",
                "border border-border/50",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              )}
            >
              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                {getSearchIcon(search.type)}
              </span>
              <span className="truncate max-w-[200px]">{search.query}</span>
              <span className="text-xs text-muted-foreground pl-1.5 border-l border-border/50">
                {formatTimestamp(search.timestamp)}
              </span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}